import React, { useCallback, useEffect, useState } from 'react';
import { Card, Button, List, Typography, Divider, message } from 'antd';
import { CheckCircleOutlined, CrownOutlined } from '@ant-design/icons';
import { SubscriptionPlan } from '../../types/subscription';
import api from '../../services/api';

const { Title, Text } = Typography;

export const SubscriptionPlans: React.FC<{ onSelectPlan: (plan: SubscriptionPlan) => void }> = ({ onSelectPlan }) => {
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [activePlanName, setActivePlanName] = useState<string>('');
  const [refreshing, setRefreshing] = useState(false);

  const fetchPlansAndStatus = useCallback(async (silent = false) => {
    try {
      setRefreshing(silent);
      const [plansResponse, statusResponse] = await Promise.allSettled([
        api.get('/teachers/subscription/plans'),
        api.get('/teachers/subscription/status'),
      ]);

      if (plansResponse.status === 'fulfilled') {
        setPlans([...plansResponse.value.data].sort((a, b) => {
          const sortDelta = Number(a.sortOrder || 0) - Number(b.sortOrder || 0);
          if (sortDelta !== 0) return sortDelta;
          return Number(a.price || 0) - Number(b.price || 0);
        }));
      } else {
        throw plansResponse.reason;
      }

      if (statusResponse.status === 'fulfilled' && statusResponse.value.data?.hasActiveSubscription) {
        setActivePlanName(statusResponse.value.data?.planName || '');
      } else {
        setActivePlanName('');
      }
    } catch (error) {
      console.error('Error fetching subscription plans:', error);
      message.error('Failed to load subscription plans');
    } finally {
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    void fetchPlansAndStatus(false);
    const intervalId = window.setInterval(() => {
      void fetchPlansAndStatus(true);
    }, 30000);
    return () => window.clearInterval(intervalId);
  }, [fetchPlansAndStatus]);

  const getPlanFeatures = (plan: SubscriptionPlan) => [
    `${plan.features.maxExamAttempts ?? plan.features.maxAssignments ?? 0} practice attempts / month`,
    plan.features.examResultsAccess ? 'Detailed exam results' : 'Basic result view',
    plan.features.certificateGeneration ? 'Certificate generation' : null,
    plan.features.advancedGrading ? 'Advanced performance insights' : null,
    plan.features.hasAnalytics ? 'Learning analytics' : null,
    plan.features.storageLimit ? `${plan.features.storageLimit} GB storage` : null,
    plan.features.canScheduleClasses ? 'Class scheduling' : null,
    `Support: ${plan.features.supportLevel}`,
  ].filter(Boolean);

  return (
    <div className="subscription-plans">
      <div className="mb-6 flex flex-col gap-3 text-center sm:flex-row sm:items-center sm:justify-between sm:text-left">
        <Title level={3} className="!mb-0">Choose Your Plan</Title>
        <Button onClick={() => void fetchPlansAndStatus(true)} loading={refreshing}>
          Refresh plans
        </Button>
      </div>
      <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
        {plans.map((plan) => {
          const isCurrentPlan =
            !!activePlanName && activePlanName.trim().toLowerCase() === plan.name.trim().toLowerCase();
          return (
            <Card
              key={plan.id}
              className={`shadow-lg transition-all hover:shadow-xl ${
                isCurrentPlan ? 'border-2 border-green-500' :
                plan.planType === 'PREMIUM' ? 'border-2 border-gold-500 transform scale-105' : ''
              }`}
              title={
                <div className="text-center">
                  {plan.planType === 'PREMIUM' && (
                    <CrownOutlined className="mr-2 text-yellow-500" />
                  )}
                  <span className="font-bold">{plan.name}</span>
                </div>
              }
              actions={[
                <Button
                  key="subscribe"
                  type={isCurrentPlan ? 'default' : plan.planType === 'PREMIUM' ? 'primary' : 'default'}
                  size="large"
                  disabled={isCurrentPlan}
                  onClick={() => !isCurrentPlan && onSelectPlan(plan)}
                  className="w-full"
                >
                  {isCurrentPlan ? 'Selected Plan' : plan.planType === 'FREE' ? 'Get Started' : 'Subscribe'}
                </Button>,
              ]}
            >
              <div className="mb-4 text-center">
                <Text className="text-3xl font-bold">
                  {plan.currency === 'USD' ? '$' : 'Rs.'}{Number(plan.price || 0).toFixed(0)}
                  <span className="text-base text-gray-500">/{plan.billingCycle.toLowerCase()}</span>
                </Text>
              </div>
              <Divider />
              <List
                size="small"
                dataSource={getPlanFeatures(plan)}
                renderItem={(feature) => (
                  <List.Item>
                    <CheckCircleOutlined className="mr-2 text-green-500" />
                    {feature}
                  </List.Item>
                )}
              />
            </Card>
          );
        })}
      </div>
    </div>
  );
};

export default SubscriptionPlans;
